import { createClient } from "@supabase/supabase-js";
import type { Course, Module, Lesson } from "@/types/course";

const SUPABASE_URL = process.env.NEXT_PUBLIC_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  throw new Error("Supabase environment variables are missing");
}

export interface LessonAccessInfo {
  hasAccess: boolean;
  isCompleted: boolean;
  previousLessonRequired?: {
    title: string;
    slug: string;
    moduleTitle: string;
  };
  assessmentRequired?: {
    type: "module" | "universal";
    title: string;
    slug: string;
    moduleTitle: string;
  };
  trackGroupRequired?: {
    trackGroupId: string;
    title: string;
    tracksCompleted: number;
    tracksRequired: number;
  };
  percentWatched: number;
  assessmentMap?: Map<string, Map<string, boolean>>;
}

export interface UniversalAssessmentRequirement {
  assessmentId: string;
  slug?: string;
  title?: string;
  completed: boolean;
}

export async function checkLessonAccess(
  courseSlug: string,
  lessonSlug: string,
  userId?: string
): Promise<LessonAccessInfo> {
  if (!userId) {
    // Guest users can only access the first lesson
    return {
      hasAccess: false,
      isCompleted: false,
      percentWatched: 0,
    };
  }

  const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
    auth: {
      persistSession: false,
    },
  });

  try {
    // Get all lesson progress for this user in this course
    const { data: progressData, error: progressError } = await supabase
      .from("course_document_lesson_progress")
      .select("lesson_id, completed, percent_watched")
      .eq("course_slug", courseSlug)
      .eq("user_id", userId);

    if (progressError) {
      console.error("Error fetching lesson progress:", progressError);
      // On error, allow access to be safe
      return {
        hasAccess: true,
        isCompleted: false,
        percentWatched: 0,
      };
    }

    // Get assessment results for this user in this course
    const { data: assessmentData, error: assessmentError } = await supabase
      .from("user_assessment_best_results")
      .select("module_id, assessment_id, passed")
      .eq("course_slug", courseSlug)
      .eq("user_id", userId);

    if (assessmentError) {
      console.error("Error fetching assessment results:", assessmentError);
    }

    // Completion threshold - consistent with the 75% threshold used in lesson progress tracking
    const COMPLETION_THRESHOLD = 0.75;

    // Create a map of lesson progress
    const progressMap = new Map(
      progressData?.map((p) => [
        p.lesson_id,
        {
          completed: p.completed || (p.percent_watched && p.percent_watched >= COMPLETION_THRESHOLD),
          percentWatched: p.percent_watched || 0,
        },
      ]) || []
    );

    // Create a map of assessment completion (module_id -> assessment_id -> passed)
    const assessmentMap = new Map<string, Map<string, boolean>>();
    assessmentData?.forEach((a) => {
      if (!assessmentMap.has(a.module_id)) {
        assessmentMap.set(a.module_id, new Map());
      }
      assessmentMap.get(a.module_id)?.set(a.assessment_id, a.passed);
    });

    return {
      hasAccess: true,
      isCompleted: progressMap.get(lessonSlug)?.completed || false,
      percentWatched: progressMap.get(lessonSlug)?.percentWatched || 0,
      assessmentMap,
    };
  } catch (error) {
    console.error("Error checking lesson access:", error);
    // On error, allow access to be safe
    return {
      hasAccess: true,
      isCompleted: false,
      percentWatched: 0,
    };
  }
}

export function checkSequentialAccess(
  course: Course,
  targetLessonSlug: string,
  progressMap: Map<string, { completed: boolean; percentWatched: number }>,
  assessmentMap?: Map<string, Map<string, boolean>>,
  universalAssessmentMap?: Map<string, UniversalAssessmentRequirement[]>,
  trackProgressMap?: Map<string, string[]>
): LessonAccessInfo {
  // Find the target lesson and its position
  let targetLessonInfo: {
    lesson: Lesson;
    module: Module;
    moduleIndex: number;
    lessonIndex: number;
  } | null = null;

  for (let moduleIndex = 0; moduleIndex < course.modules.length; moduleIndex++) {
    const courseModule = course.modules[moduleIndex];
    for (let lessonIndex = 0; lessonIndex < courseModule.lessons.length; lessonIndex++) {
      const lesson = courseModule.lessons[lessonIndex];
      if (lesson.slug === targetLessonSlug) {
        targetLessonInfo = { lesson, module: courseModule, moduleIndex, lessonIndex };
        break;
      }
    }
    if (targetLessonInfo) break;
  }

  if (!targetLessonInfo) {
    return {
      hasAccess: false,
      isCompleted: false,
      percentWatched: 0,
    };
  }

  const { lesson, module, moduleIndex, lessonIndex } = targetLessonInfo;

  // Check if current lesson is completed
  const currentProgress = progressMap.get(lesson.slug);
  const isCompleted = currentProgress?.completed || false;
  const percentWatched = currentProgress?.percentWatched || 0;

  // First lesson is always accessible
  if (moduleIndex === 0 && lessonIndex === 0) {
    return {
      hasAccess: true,
      isCompleted,
      percentWatched,
    };
  }

  // Check if previous lesson is completed
  let previousLesson: Lesson;
  let previousModule: Module;

  if (lessonIndex > 0) {
    // Previous lesson is in the same module
    previousLesson = module.lessons[lessonIndex - 1];
    previousModule = module;
  } else {
    // This is the first lesson of a module
    // Check if previous module has required assessments that must be passed
    if (moduleIndex === 0) {
      // This is the first lesson of the first module, so it should be accessible
      return {
        hasAccess: true,
        isCompleted,
        percentWatched,
      };
    }

    // If current module is a track, find the module before the track group
    // (not the previous track in the same group, since tracks are independent)
    if (module.isTrack && module.trackGroupId) {
      const trackGroup = course.trackGroups?.find((tg) => tg.id === module.trackGroupId);
      if (trackGroup) {
        // Find the module that comes before this track group
        // (the first non-track module before any track in this group)
        const trackModuleIndices = trackGroup.trackModuleIds
          .map((id) => course.modules.findIndex((m) => m.id === id))
          .filter((idx) => idx >= 0);

        if (trackModuleIndices.length === 0) {
          return {
            hasAccess: false,
            isCompleted,
            percentWatched,
          };
        }

        const firstTrackIndex = Math.min(...trackModuleIndices);

        if (firstTrackIndex > 0) {
          // Find the previous non-track module before the track group
          let prevModuleIndex = firstTrackIndex - 1;
          while (prevModuleIndex >= 0 && course.modules[prevModuleIndex].isTrack) {
            prevModuleIndex--;
          }

          if (prevModuleIndex >= 0) {
            previousModule = course.modules[prevModuleIndex];
            previousLesson = previousModule.lessons[previousModule.lessons.length - 1];
          } else {
            // No previous module before tracks, allow access
            return {
              hasAccess: true,
              isCompleted,
              percentWatched,
            };
          }
        } else {
          // Track group is at the beginning, allow access
          return {
            hasAccess: true,
            isCompleted,
            percentWatched,
          };
        }
      } else {
        // Track group not found, fall back to normal behavior
        previousModule = course.modules[moduleIndex - 1];
        previousLesson = previousModule.lessons[previousModule.lessons.length - 1];
      }
    } else {
      // Check if there's a track group between previous module and current module
      const trackGroupBeforeCurrentModule = findTrackGroupBeforeModule(course, moduleIndex);

      if (trackGroupBeforeCurrentModule && trackProgressMap) {
        const completedTrackModuleIds = trackProgressMap.get(trackGroupBeforeCurrentModule.id) || [];
        const completedSet = new Set(completedTrackModuleIds);

        if (!checkTrackGroupCompletion(trackGroupBeforeCurrentModule, completedSet)) {
          return {
            hasAccess: false,
            isCompleted,
            percentWatched,
            trackGroupRequired: {
              trackGroupId: trackGroupBeforeCurrentModule.id,
              title: trackGroupBeforeCurrentModule.title,
              tracksCompleted: completedTrackModuleIds.length,
              tracksRequired: trackGroupBeforeCurrentModule.requiredTracksCount,
            },
          };
        }

        // Track requirements are met - find the module BEFORE the track group
        // (not a track module, since user may have completed a different track)
        const trackModuleIndices = trackGroupBeforeCurrentModule.trackModuleIds
          .map((id) => course.modules.findIndex((m) => m.id === id))
          .filter((idx) => idx >= 0);

        if (trackModuleIndices.length === 0) {
          return {
            hasAccess: false,
            isCompleted,
            percentWatched,
            trackGroupRequired: {
              trackGroupId: trackGroupBeforeCurrentModule.id,
              title: trackGroupBeforeCurrentModule.title,
              tracksCompleted: completedTrackModuleIds.length,
              tracksRequired: trackGroupBeforeCurrentModule.requiredTracksCount,
            },
          };
        }

        const firstTrackIndex = Math.min(...trackModuleIndices);

        if (firstTrackIndex > 0) {
          // Find the previous non-track module before the track group
          let prevModuleIndex = firstTrackIndex - 1;
          while (prevModuleIndex >= 0 && course.modules[prevModuleIndex].isTrack) {
            prevModuleIndex--;
          }

          if (prevModuleIndex >= 0) {
            previousModule = course.modules[prevModuleIndex];
            previousLesson = previousModule.lessons[previousModule.lessons.length - 1];
          } else {
            // No previous module before tracks, allow access
            return {
              hasAccess: true,
              isCompleted,
              percentWatched,
            };
          }
        } else {
          // Track group is at the beginning, allow access
          return {
            hasAccess: true,
            isCompleted,
            percentWatched,
          };
        }
      } else {
        previousModule = course.modules[moduleIndex - 1];
        previousLesson = previousModule.lessons[previousModule.lessons.length - 1];
      }
    }

    // Check if previous module has required assessments
    const requiredAssessments = previousModule.assessments?.filter((a) => a.required);
    if (requiredAssessments && requiredAssessments.length > 0 && assessmentMap) {
      for (const assessment of requiredAssessments) {
        const moduleAssessments = assessmentMap.get(previousModule.id);
        const assessmentPassed = moduleAssessments?.get(assessment.id);

        if (!assessmentPassed) {
          // User must pass this assessment before accessing the next module
          return {
            hasAccess: false,
            isCompleted,
            percentWatched,
            assessmentRequired: {
              type: "module",
              title: assessment.title,
              slug: assessment.slug,
              moduleTitle: previousModule.title,
            },
          };
        }
      }
    }

    const requiredUniversalAssessments = universalAssessmentMap?.get(previousModule.id) ?? [];
    for (const requirement of requiredUniversalAssessments) {
      if (!requirement.completed) {
        return {
          hasAccess: false,
          isCompleted,
          percentWatched,
          assessmentRequired: {
            type: "universal",
            title: requirement.title ?? "Universal assessment",
            slug: requirement.slug ?? requirement.assessmentId,
            moduleTitle: previousModule.title,
          },
        };
      }
    }
  }

  const previousProgress = progressMap.get(previousLesson.slug);
  const previousCompleted = previousProgress?.completed || false;

  if (!previousCompleted) {
    return {
      hasAccess: false,
      isCompleted,
      percentWatched,
      previousLessonRequired: {
        title: previousLesson.title,
        slug: previousLesson.slug,
        moduleTitle: previousModule.title,
      },
    };
  }

  return {
    hasAccess: true,
    isCompleted,
    percentWatched,
  };
}

/**
 * Check if a track group's completion requirements are met
 */
export function checkTrackGroupCompletion(
  trackGroup: import("@/types/course").TrackGroup,
  completedModuleIds: Set<string>
): boolean {
  const completedTracksCount = trackGroup.trackModuleIds.filter((moduleId) =>
    completedModuleIds.has(moduleId)
  ).length;

  return completedTracksCount >= trackGroup.requiredTracksCount;
}

/**
 * Find if there's a track group that should come before a given module
 * based on order and track module IDs
 */
export function findTrackGroupBeforeModule(
  course: Course,
  moduleIndex: number
): import("@/types/course").TrackGroup | null {
  if (!course.trackGroups || course.trackGroups.length === 0) {
    return null;
  }

  const currentModule = course.modules[moduleIndex];
  if (!currentModule) {
    return null;
  }

  // If the current module is itself a track, allow access to all tracks in the group
  if (currentModule.isTrack) {
    return null;
  }

  // Find track groups that should come before this module
  // A track group comes before if its order is less than the current module's order
  // and it's not part of the current module's track group
  for (const trackGroup of course.trackGroups) {
    // Check if any of the track modules in this group have an order that
    // comes before the current module
    const trackModules = course.modules.filter((m) =>
      trackGroup.trackModuleIds.includes(m.id)
    );

    if (trackModules.length === 0) {
      continue;
    }

    const trackOrder = Math.min(...trackModules.map((module) => module.order));

    // If track group's order is less than current module's order,
    // this track group should be completed before accessing current module
    if (trackOrder !== undefined && trackOrder < currentModule.order) {
      return trackGroup;
    }
  }

  return null;
}

/**
 * Check if all lessons in a module are completed and required assessments are passed
 */
export function getModuleCompletionStatus(
  module: Module,
  progressMap: Map<string, { completed: boolean; percentWatched: number }>,
  assessmentMap?: Map<string, Map<string, boolean>>
): boolean {
  // Check if all lessons are completed
  const allLessonsCompleted = module.lessons.every((lesson) => {
    const progress = progressMap.get(lesson.slug);
    return progress?.completed || false;
  });

  if (!allLessonsCompleted) {
    return false;
  }

  // Check if required assessments are passed
  const requiredAssessments = module.assessments?.filter((a) => a.required);
  if (requiredAssessments && requiredAssessments.length > 0 && assessmentMap) {
    const moduleAssessments = assessmentMap.get(module.id);
    for (const assessment of requiredAssessments) {
      const passed = moduleAssessments?.get(assessment.id);
      if (!passed) {
        return false;
      }
    }
  }

  return true;
}
